// 🔧 my_sponsors.js
// ✅ Purpose: Sponsor-contact dashboard for assigned event sponsors
// ✅ Features:
// - Uses useEventContext() for event-scoped routing
// - Requires logged-in user
// - Loads /events/{eventId}/users/{uid}
// - Reads assigned sponsorIds from event user record
// - Shows sponsor cards for assigned sponsors
// - Links sponsor contacts to edit sponsor public information
// - Shows Sponsor Award Title when available
// - Shows clear empty/permission messaging
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { useAuth } from "../../auth_context";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";

const MySponsors = () => {

  const { user, authLoading } = useAuth();

  const {
    eventslug,
    eventId,
    eventLoading,
    ready,
    eventDoc,
  } = useEventContext({ redirectIfNotFound: true });

  const [eventUserData, setEventUserData] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAssignedSponsors = async () => {
      if (!eventId || !user?.uid) return;

      setLoading(true);

      try {
        const eventUserSnap = await getDoc(
          doc(db, "events", eventId, "users", user.uid)
        );

        const eventUser = eventUserSnap.exists()
          ? eventUserSnap.data()
          : null;

        setEventUserData(eventUser);

        const sponsorIds = eventUser?.sponsorIds || [];

        if (!eventUser?.sponsorContact || sponsorIds.length === 0) {
          setSponsors([]);
          return;
        }

        const sponsorResults = [];

        for (const sponsorId of sponsorIds) {
          const sponsorSnap = await getDoc(
            doc(db, "events", eventId, "sponsors", sponsorId)
          );

          if (sponsorSnap.exists()) {
            sponsorResults.push({
              id: sponsorSnap.id,
              ...sponsorSnap.data(),
            });
          }
        }

        setSponsors(sponsorResults);

        logger.log("🌟 Sponsor contact sponsors loaded", {
          eventId,
          uid: user.uid,
          count: sponsorResults.length,
        });
      } catch (err) {
        logger.error("❌ Failed to load assigned sponsors:", {
          value: err,
        });

        setSponsors([]);
      } finally {
        setLoading(false);
      }
    };

    loadAssignedSponsors();
  }, [eventId, user]);

  // =====================================================
  // 🎗 Sponsors Add-on Page Gate
  // =====================================================
  // ✅ Purpose:
  // - Prevent direct URL access to sponsor pages when the Sponsors add-on is inactive
  // - Keeps public, sponsor-contact, and manager sponsor tools hidden
  // - Friendly message instead of exposing sponsor functionality
  // =====================================================
  const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor");

  if (!sponsorsEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-info text-center mb-0">
            Sponsors are not active for this event.
          </div>
        </div>
      </section>
    );
  }

  const mySponsorsSeo = {
    title: `My Sponsors | ${eventDoc?.name || "Event"} | Let's Go Vote`,
    description:
      "Manage sponsor information assigned to your sponsor contact account.",
    path: `/${eventslug}/my_sponsors`,
    noindex: true,
  };

  if (!ready || authLoading || eventLoading || loading) {
    return <FullPageSpinner message="Loading My Sponsors..." />;
  }

  if (!user) {
    return (
    <>
    <SeoHead {...mySponsorsSeo} />
      <section id="top-content" className="container-fluid py-5">
        <div className="white-section p-4 rounded shadow-sm bg-white">
          <div className="alert alert-warning mb-0">
            Please log in to view your sponsor assignments.
          </div>
        </div>
      </section>
      </>
    );
  }

  if (!eventUserData?.sponsorContact) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="white-section p-4 rounded shadow-sm bg-white">
          <div className="alert alert-info mb-0">
            You are not currently listed as a sponsor contact for this event.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🌟 My Sponsors</h1>

          <p className="lead text-muted">
            Review and update the sponsor information assigned to you.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <div className="alert alert-info">
          Please validate the sponsor details, logo, website, description, and award information.
          This information may be shown publicly to event attendees.
        </div>

        {sponsors.length === 0 ? (
          <div className="alert alert-warning mb-0">
            You are marked as a sponsor contact, but no sponsor records are currently assigned to you.
          </div>
        ) : (
          <div className="row">
            {sponsors.map((sponsor) => (
              <div key={sponsor.id} className="col-12 col-md-6 col-lg-4 mb-4">
                <div className="card h-100 shadow-sm border-0">
                  <div className="card-body">
                    <div
                      className="bg-white border rounded d-flex align-items-center justify-content-center mb-3"
                      style={{ minHeight: "120px" }}
                    >
                      {sponsor.logoUrl ? (
                        <img
                          src={sponsor.logoUrl}
                          alt={sponsor.name || "Sponsor logo"}
                          className="img-fluid"
                          style={{
                            maxHeight: "100px",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <span className="text-muted">No Logo</span>
                      )}
                    </div>

                    <h5 className="fw-bold">
                      {sponsor.name || "Unnamed Sponsor"}
                    </h5>

                    <p className="mb-1">
                      <strong>Level:</strong>{" "}
                      {sponsor.level || "Event Sponsors"}
                    </p>

                    <p className="mb-3">
                      <strong>Award:</strong>{" "}
                      {sponsor.sponsorAwardTitle || (
                        <span className="text-warning">
                          Missing Sponsor Award Title
                        </span>
                      )}
                    </p>

                    <Link
                      to={`/${eventslug}/sponsor/${sponsor.sponsorSlug || sponsor.id}`}
                      className="btn btn-primary btn-sm"
                    >
                      ✏️ Review / Update Sponsor
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MySponsors;
